import { useCallback, useMemo } from "react";
import { DefaultRadioGroupInput } from "../../../../../../common/components/default_radio_group_input";
import { ObraChecklistServicoEstatisticas } from "../../../../domain/models/obra_checklist_servico_estatiticas";
import { PercentageMaskedInput } from "../../../../../../common/components/masked_inputs/percentage_masked_input";

import CascateList from "../../../../../../common/components/cascate_list";

/**
 * StatisticsPanel
 */

type Props = {
  statusChecklistArea: number | null;
  statusChecklistServico: number;
  estatisticasChecklistServico: ObraChecklistServicoEstatisticas;
  metaChecklistServico: number;
  onChangeStatus: (status: number) => void;
  onBlurStatus: () => void;
  onChangeMeta: (meta: number) => void;
};

export const StatisticsPanel = ({
  statusChecklistArea,
  statusChecklistServico,
  estatisticasChecklistServico,
  metaChecklistServico,
  onChangeStatus,
  onBlurStatus,
  onChangeMeta,
}: Props) => {
  const statusOptions = useMemo(() => {
    return [
      {
        id: 0,
        label: 'Em Andamento',
        style: {
          backgroundColor: '#f8cb00',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 1,
        label: 'Finalizada',
        style: {
          backgroundColor: '#f3565d',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
    ];
  }, [
  ]);

  const computeRatio = useCallback((total: number | null, index: number | null) => {
    let statistic = 0;
    if (total && index) {
      statistic = (index / total) * 100;
    }
    return statistic.toFixed(2);
  }, [
  ]);

  const fixedPercentual = useMemo(() => {
    if (metaChecklistServico) {
      return metaChecklistServico * 100;
    }
    return 0;
  }, [
    metaChecklistServico,
  ]);

  const statusMetaClassName = useMemo(() => {
    const { total, totalA } = estatisticasChecklistServico;
    if (total && totalA && metaChecklistServico) {
      if (total > 0 && ((totalA / total) * 100 >= metaChecklistServico)) {
        return "green";
      }
    }
    return "red";
  }, [
    estatisticasChecklistServico,
    metaChecklistServico,
  ]);

  const statusMeta = useMemo(() => {
    const { total, totalA } = estatisticasChecklistServico;
    if (total && totalA && metaChecklistServico) {
      if (total > 0 && ((totalA / total) * 100 >= metaChecklistServico)) {
        return "Acima da meta";
      }
    }
    return "Abaixo da meta";
  }, [
    estatisticasChecklistServico,
    metaChecklistServico,
  ]);

  return (
    <CascateList title="Informações Gerais" defaultOpen>
      <div className="w-full flex flex-col gap-2">
        <DefaultRadioGroupInput
          name='tipo'
          label='Status'
          options={statusOptions}
          disabled={statusChecklistArea === 1}
          value={statusChecklistServico}
          onValueChange={e => e ? onChangeStatus(Number(e)) : null}
        />

        <div className="grid grid-cols-2 gap-4">
          <PercentageMaskedInput
            name='meta'
            label='Meta (Aprovações)'
            disabled={statusChecklistArea === 1}
            value={fixedPercentual}
            onChangeValue={e => e ? onChangeMeta(Number(e) / 100) : null}
            onBlur={onBlurStatus}
          />

          <div className="flex flex-row items-center text-black dark:text-white text-lg">
            <span className="font-bold me-2">Status Meta: </span>
            <span className="font-bold" style={{ color: statusMetaClassName }}>{statusMeta}</span>
          </div>
        </div>

        <table className="table-auto mt-4">
          <thead>
            <tr>
              <th colSpan={5}></th>
              <th colSpan={5} className="bg-[#e9edef] dark:bg-[#6a7981] text-black dark:text-white">Total</th>
            </tr>
          </thead>
          <thead>
            <tr>
              <th className="bg-[#e9edef] dark:bg-[#6a7981] text-black dark:text-white">Total Inspeções</th>
              <th className="bg-[#e9edef] dark:bg-[#6a7981] text-black dark:text-white">I. Aprovação(%)</th>
              <th className="bg-[#e9edef] dark:bg-[#6a7981] text-black dark:text-white">I. Retrabalho(%)</th>
              <th className="bg-[#e9edef] dark:bg-[#6a7981] text-black dark:text-white">I. Retrabalho Aprovado(%)</th>
              <th className="bg-[#e9edef] dark:bg-[#6a7981] text-black dark:text-white">I. Reprovação(%)</th>
              <th className="bg-[#e9edef] dark:bg-[#6a7981] text-black dark:text-white min-w-[3rem] max-w-[5rem]">NA</th>
              <th className="bg-[#e9edef] dark:bg-[#6a7981] text-black dark:text-white min-w-[3rem] max-w-[5rem]">A</th>
              <th className="bg-[#e9edef] dark:bg-[#6a7981] text-black dark:text-white min-w-[3rem] max-w-[5rem]">R</th>
              <th className="bg-[#e9edef] dark:bg-[#6a7981] text-black dark:text-white min-w-[3rem] max-w-[5rem]">RA</th>
              <th className="bg-[#e9edef] dark:bg-[#6a7981] text-black dark:text-white min-w-[3rem] max-w-[5rem]">X</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="bg-[#DCFDF7] h-[1.8rem]">
                <div className='size-full flex flex-row justify-center items-center font-bold'>
                  {estatisticasChecklistServico.total}
                </div>
              </td>
              <td className="bg-[#DCFDF7] h-[1.8rem]">
                <div className='size-full flex flex-row justify-center items-center font-bold'>
                  <span>{computeRatio(estatisticasChecklistServico.total, estatisticasChecklistServico.totalA)} %</span>
                </div>
              </td>
              <td className="bg-[#DCFDF7] h-[1.8rem]">
                <div className='size-full flex flex-row justify-center items-center font-bold'>
                  <span>{computeRatio(estatisticasChecklistServico.total, estatisticasChecklistServico.totalR)} %</span>
                </div>
              </td>
              <td className="bg-[#DCFDF7] h-[1.8rem]">
                <div className='size-full flex flex-row justify-center items-center font-bold'>
                  <span>{computeRatio(estatisticasChecklistServico.totalRA, estatisticasChecklistServico.totalR)} %</span>
                </div>
              </td>
              <td className="bg-[#DCFDF7] h-[1.8rem]">
                <div className='size-full flex flex-row justify-center items-center font-bold'>
                  <span>{computeRatio(estatisticasChecklistServico.total, estatisticasChecklistServico.totalX)} %</span>
                </div>
              </td>
              <td className="bg-[#8EA3DF] h-[1.8rem]">
                <div className='size-full flex flex-row justify-center items-center font-bold'>
                  <span>{estatisticasChecklistServico.totalNA}</span>
                </div>
              </td>
              <td className="bg-[#26c281] h-[1.8rem]">
                <div className='size-full flex flex-row justify-center items-center font-bold'>
                  {estatisticasChecklistServico.totalA}
                </div>
              </td>
              <td className="bg-[#ffff00] h-[1.8rem]">
                <div className='size-full flex flex-row justify-center items-center font-bold'>
                  {estatisticasChecklistServico.totalR}
                </div>
              </td>
              <td className="bg-[#ffb848] h-[1.8rem]">
                <div className='size-full flex flex-row justify-center items-center font-bold'>
                  {estatisticasChecklistServico.totalRA}
                </div>
              </td>
              <td className="bg-[#ef4836] h-[1.8rem]">
                <div className='size-full flex flex-row justify-center items-center font-bold'>
                  {estatisticasChecklistServico.totalX}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </CascateList>
  );
}